import firebase from "firebase/app";
import _ from "lodash";
import { ImageURISource, LayoutAnimation, Platform } from "react-native";
import { sanitizeUrl } from "@braintree/sanitize-url";
import { tCartProduct } from "tmt_api/src/types/types.validator";
import { kSrcSizes } from "./constants";
import { RecursivePartial } from "./types";

type tpToggleInArray = <T>(myArray: Array<T>, myItem: T) => Array<T>;
export const toggleInArray: tpToggleInArray = (myArray, myItem) => {
  const aIndex = myArray.indexOf(myItem);
  let aResult: typeof myArray;

  if (aIndex > -1) {
    aResult = myArray.slice(0, aIndex).concat(myArray.slice(aIndex + 1, myArray.length));
  } else {
    aResult = myArray.concat(myItem);
  }

  return aResult;
};

export const getItemPrice = (myCartItem: tCartProduct): number => {
  if (myCartItem.variants.length !== 0) {
    const variantValue =
      myCartItem.variants.find((myVariant) => myVariant.sku === myCartItem.actualSKU)?.wholesalePrice ??
      myCartItem.variants.find((myVariant) => myVariant.tester?.sku === myCartItem.actualSKU)?.tester?.price;
    return myCartItem.quantity * (variantValue ?? 0);
  }
  return (
    myCartItem.quantity *
    (myCartItem.actualSKU === myCartItem.tester?.sku ? myCartItem.tester?.price ?? 0 : myCartItem.wholesale_price)
  );
};

type tpBound = (lower: number, upper: number, value: number) => number;
export const bound: tpBound = (lower, upper, value) => {
  return Math.min(upper, Math.max(lower, value));
};

/** Immutably clone with CONCAT | REPLACE of arrays */
export const mergeWithArrays = <T>(
  myDest: T,
  myStrategy: "CONCAT" | "REPLACE" | "MERGE",
  ...mySources: RecursivePartial<T>[]
): T => {
  const aClone = _.cloneDeep(myDest);
  if (myStrategy === "MERGE") return _.merge(myDest, ...mySources);
  else
    return _.mergeWith(aClone, ...mySources, (myDestArray: unknown[], mySource: ConcatArray<unknown>) => {
      if (Array.isArray(myDestArray) && Array.isArray(mySource)) {
        return myStrategy === "CONCAT" ? myDestArray.concat(mySource) : mySource;
      } else return undefined;
    });
};

export type tSrcSet = {
  [K in typeof kSrcSizes[number]]: string;
};

const checkWebpSupport = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (Platform.OS === "android" || Platform.OS === "ios") resolve(true);
    const img = new Image();
    img.onload = () => {
      resolve(img.width > 0 && img.height > 0);
    };
    img.onerror = () => {
      resolve(false);
    };
    img.src = "data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==";
  });
};

export const getSrcSet = async (mySource: string): Promise<tSrcSet> => {
  const aEndPattern = /^(.*%2F)([^.?]+).*$/;

  const aMatch = aEndPattern.exec(mySource);

  if (aMatch === null) {
    if (mySource.includes("firebase")) {
      console.warn("getSrcSet: skipping source", mySource);
    }
    return {
      300: mySource,
      512: mySource,
      1024: mySource,
      2048: mySource,
    };
  }

  const isBrowserSupportWebp = await checkWebpSupport();
  const extesion = isBrowserSupportWebp ? ".webp?alt=media" : ".jpeg?alt=media";

  return {
    300: `${aMatch[1]}resized%2F${aMatch[2]}_${300}x${300}${extesion}`,
    512: `${aMatch[1]}resized%2F${aMatch[2]}_${512}x${512}${extesion}`,
    1024: `${aMatch[1]}resized%2F${aMatch[2]}_${1024}x${1024}${extesion}`,
    2048: `${aMatch[1]}resized%2F${aMatch[2]}_${2048}x${2048}${extesion}`,
  };
};

export const getSourceSet = async (source: string): Promise<ImageURISource[]> => {
  const resolutions = await getSrcSet(source);
  return Object.entries(resolutions).map(([mySize, mySource]) => ({
    uri: mySource,
    width: Number(mySize),
    height: Number(mySize),
  }));
};

export const getSrcSetString = async (mySource: string): Promise<string> => {
  const aSrcSet = await getSrcSet(mySource);

  let aSrcSetStr = Object.entries(aSrcSet).reduce((prev, [key, value]) => {
    return prev + `${value} ${key}w, `;
  }, "");
  aSrcSetStr = aSrcSetStr.substring(0, aSrcSetStr.length - 2);

  return aSrcSetStr;
};

export const safeMap = <T>(
  myData: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>[],
  myValidator: (value: unknown) => T,
): Array<T> => {
  const myDataArray: Array<T> = [];
  myData.forEach((element) => {
    try {
      myDataArray.push(myValidator(element.data()));
    } catch (e) {
      console.error(e);
    }
  });
  return myDataArray;
};

export const numberToString = (value: string | number): string => {
  if (typeof value === "number") return value.toString();

  return value;
};

export const UseAnimation = (): void => {
  LayoutAnimation.configureNext(
    LayoutAnimation.create(1000, LayoutAnimation.Types.easeInEaseOut, LayoutAnimation.Properties.scaleXY),
  );
};

export const isSafari = (): boolean =>
  Platform.OS === "web" && navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome");

export const getSafeUrl = (value?: string): string => {
  if (!value) return "";
  const regexp = /^(http:\/\/|https:\/\/).*$/g;
  let safeValue = sanitizeUrl(value);
  if (!safeValue.match(regexp)) {
    safeValue = `https://${safeValue}`;
  }
  return safeValue;
};

export const copyLinkToClipboard = (url: string, title?: string): void => {
  const text = title || url;
  if (typeof ClipboardItem !== "undefined") {
    const item = new ClipboardItem({
      "text/html": Promise.resolve(new Blob([`<a href="${url}">${text}</a>`], { type: "text/html" })),
      "text/plain": Promise.resolve(new Blob([url], { type: "text/plain" })),
    });
    navigator.clipboard.write([item]);
  } else {
    navigator.clipboard.writeText(url);
  }
};
